import { mapOptions } from "./sidebar.utils";

/**
 * Sidebar config: section order + icons only.
 * New route in existing section: add sidebarGroup, sidebarLabel (and optional countKey) on the route — no change here.
 * New section: add one entry below (optionsKey must match route.sidebarGroup).
 */
/** Section definitions: order, label, icon. optionsKey = sidebarGroup from routes. */
const SECTION_DEFS = [
  {
    label: "Control Tower",
    optionsKey: "controlTower",
    labelSvgSize: 20,
    labelSvgType: "graph",
  },
  {
    label: "Reports",
    optionsKey: "reports",
    labelSvgSize: 20,
    labelSvgType: "report-icon",
  },
  {
    label: "Driver Partners",
    optionsKey: "driverList",
    labelSvgSize: 20,
    labelSvgType: "driver-icon",
  },
  {
    label: "Fleet",
    optionsKey: "parkingList",
    labelSvgSize: 20,
    labelSvgType: "fleet-icon",
  },
  {
    label: "Requests",
    optionsKey: "requestList",
    labelSvgSize: 20,
    labelSvgType: "request-icon",
  },
  {
    label: "Maintenance",
    optionsKey: "maintenanceList",
    labelSvgSize: 18,
    labelSvgType: "maintenance-sidebar-icon",
  },
  {
    label: "Tally",
    optionsKey: "tallyList",
    labelSvgSize: 20,
    labelSvgType: "tally-icon",
  },
  {
    label: "Users",
    optionsKey: "userList",
    labelSvgSize: 20,
    labelSvgType: "user-icon",
  },
  {
    label: "Settlement",
    optionsKey: "updates",
    labelSvgSize: 20,
    labelSvgType: "settlement-icon",
  },
  {
    label: "Performance",
    optionsKey: "performance",
    labelSvgSize: 20,
    labelSvgType: "performance-icon",
  },
];

function buildRoute(route, params) {
  const l = route.replace(/:([^/]+)/g, (_, key) => {
    return params[key] ?? `:${key}`; // fallback if key not found
  });
  return l;
}

/**
 * Build option items from route-level sidebar metadata.
 * routesPermissions: array of { path, authorized, sidebarGroup?, sidebarLabel?, countKey?, countKeyAllFleet? }
 * Returns { [optionsKey]: [{ sidebarListClick, text, countKey?, countKeyAllFleet? }, ...] }
 */
function getOptionsByGroupFromRoutes(routesPermissions) {
  const withSidebar = (routesPermissions || []).filter(
    (r) => r.sidebarGroup && r.sidebarLabel != null
  );
  const byGroup = {};
  withSidebar.forEach((r) => {
    const key = r.sidebarGroup;
    if (!byGroup[key]) byGroup[key] = [];
    byGroup[key].push({
      ...r,
      sidebarListClick: r.sidebarPath ?? r.path,
      sidebarBasePath: r.sidebarBasePath,
      text: r.sidebarLabel,
      countKey: r.countKey,
      countKeyAllFleet: r.countKeyAllFleet,
    });
  });
  return byGroup;
}

/**
 * Returns all option lists with counts applied, derived from route metadata.
 * allFleetRecordCount is from allFleetHubAPIDetails.recordCounts (used for "images upload history").
 */
export function getSidebarLists(recordCounts, allFleetRecordCount, routesPermissions) {
  const byGroup = getOptionsByGroupFromRoutes(routesPermissions);
  const result = {};
  SECTION_DEFS.forEach((section) => {
    const items = byGroup[section.optionsKey] || [];
    result[section.optionsKey] = mapOptions(
      recordCounts,
      section.optionsKey === "requestList" ? (allFleetRecordCount ?? 0) : 0,
      items
    );
  });
  return result;
}

/**
 * Build full sideBarList: sections with options pre-filtered by role.
 * Routes with authorized: [] are treated as "allow all" (same as AuthorizedRoute).
 */
export function getSideBarList(recordCounts, allFleetRecordCount, role, routesPermissions) {
  const lists = getSidebarLists(recordCounts, allFleetRecordCount, routesPermissions);
  /** Gate access by sidebarGroup so duplicate paths from other features do not grant visibility. */
  const canAccess = (path, sidebarGroup) =>
    routesPermissions?.some((r) => {
      const routePath = r.sidebarPath ?? r.path;
      const baseMatch = r.sidebarBasePath && path.startsWith(r.sidebarBasePath);
      return (
        (r.path === path || routePath === path || baseMatch) &&
        r.sidebarGroup === sidebarGroup &&
        (!r.authorized?.length || r.authorized.includes(role))
      );
    });

  let result = SECTION_DEFS.map((section) => {
    const allOptions = lists[section.optionsKey] || [];

    const options = role
      ? allOptions.filter((opt) => canAccess(opt.sidebarListClick, section.optionsKey))
      : [];
    const updatedRoutes = options.map((opt) => {
      const hasParams = /:[^/]+/.test(opt.path);
      const linkPath =
        opt.sidebarPath != null
          ? opt.sidebarPath
          : hasParams
            ? buildRoute(opt.path, opt)
            : opt.sidebarListClick;
      return {
        ...opt,
        sidebarListClick: linkPath,
        sidebarBasePath: opt.sidebarBasePath,
      };
    });
    return {
      ...section,
      options: updatedRoutes,
      display: options.length > 0 ? "block" : "none",
    };
  });

  return result;
}
